// Security test suite for React component usage
import { 
  sanitizeHtml, 
  sanitizeText, 
  sanitizeInput, 
  sanitizeEmail, 
  sanitizePhone, 
  sanitizeFilename,
  sanitizeUrl,
  containsDangerousContent,
  validateLength,
  validateFile,
  escapeHtml
} from './sanitization';
import { 
  VALIDATION_PATTERNS, 
  validatePasswordStrength, 
  isSafeUrl,
  generateSecureRandom,
  PASSWORD_REQUIREMENTS 
} from './security';

interface TestResult {
  testName: string;
  passed: boolean;
  error?: string;
  details?: any;
}

class SecurityTestSuite {
  private results: TestResult[] = [];

  private addResult(testName: string, passed: boolean, error?: string, details?: any) {
    this.results.push({ testName, passed, error, details });
    console.log(`${passed ? '✅' : '❌'} ${testName}${error ? ` - ${error}` : ''}`);
  }

  // Test HTML sanitization
  testHtmlSanitization() {
    console.log('\n🧪 Testing HTML Sanitization...');
    
    const testCases = [
      {
        input: '<script>alert("xss")</script><p>Safe content</p>',
        expected: '<p>Safe content</p>',
        description: 'Remove script tags'
      },
      {
        input: '<iframe src="malicious.com"></iframe><h1>Title</h1>',
        expected: '<h1>Title</h1>',
        description: 'Remove iframe tags'
      },
      {
        input: '<p onclick="alert(1)">Click me</p>',
        expected: '<p>Click me</p>',
        description: 'Remove event handlers'
      },
      {
        // eslint-disable-next-line no-script-url
        input: '<a href="javascript:alert(1)">Link</a>',
        expected: '<a>Link</a>',
        description: 'Remove javascript: URLs'
      },
      {
        input: '<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==">',
        expected: '<img>',
        description: 'Remove data: URLs'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizeHtml(input);
      const passed = result === expected;
      this.addResult(`HTML Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test text sanitization
  testTextSanitization() {
    console.log('\n🧪 Testing Text Sanitization...');
    
    const testCases = [
      {
        input: 'Hello <script>alert("xss")</script> World',
        expected: 'Hello  World',
        description: 'Remove angle brackets'
      },
      {
        // eslint-disable-next-line no-script-url
        input: 'Visit javascript:alert(1) for more info',
        expected: 'Visit  for more info',
        description: 'Remove javascript: URLs'
      },
      {
        input: 'Click onclick=alert(1) to continue',
        expected: 'Click  to continue',
        description: 'Remove event handlers'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizeText(input);
      const passed = result === expected;
      this.addResult(`Text Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test email sanitization
  testEmailSanitization() {
    console.log('\n🧪 Testing Email Sanitization...');
    
    const testCases = [
      {
        input: '  USER@EXAMPLE.COM  ',
        expected: 'user@example.com',
        description: 'Normalize case and trim'
      },
      {
        input: 'user@example.com<script>',
        expected: 'user@example.com',
        description: 'Remove dangerous content'
      },
      {
        input: 'user@example.com',
        expected: 'user@example.com',
        description: 'Valid email unchanged'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizeEmail(input);
      const passed = result === expected;
      this.addResult(`Email Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test phone sanitization
  testPhoneSanitization() {
    console.log('\n🧪 Testing Phone Sanitization...');
    
    const testCases = [
      {
        input: '+1 (555) 123-4567',
        expected: '+1 (555) 123-4567',
        description: 'Valid phone number'
      },
      {
        input: '555-123-4567<script>',
        expected: '555-123-4567',
        description: 'Remove dangerous content'
      },
      {
        input: '555abc123def456',
        expected: '555123456',
        description: 'Remove non-phone characters'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizePhone(input);
      const passed = result === expected;
      this.addResult(`Phone Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test filename sanitization
  testFilenameSanitization() {
    console.log('\n🧪 Testing Filename Sanitization...');
    
    const testCases = [
      {
        input: 'file<script>.txt',
        expected: 'file.txt',
        description: 'Remove dangerous characters'
      },
      {
        input: '../../../etc/passwd',
        expected: 'etcpasswd',
        description: 'Remove directory traversal'
      },
      {
        input: 'file with spaces.txt',
        expected: 'file with spaces.txt',
        description: 'Keep valid filename'
      },
      {
        input: 'file.txt',
        expected: 'file.txt',
        description: 'Valid filename unchanged'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizeFilename(input);
      const passed = result === expected;
      this.addResult(`Filename Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test URL sanitization
  testUrlSanitization() {
    console.log('\n🧪 Testing URL Sanitization...');
    
    const testCases = [
      {
        input: 'https://example.com',
        expected: 'https://example.com',
        description: 'Valid HTTPS URL'
      },
      {
        input: 'http://example.com',
        expected: 'http://example.com',
        description: 'Valid HTTP URL'
      },
      {
        // eslint-disable-next-line no-script-url
        input: 'javascript:alert(1)',
        expected: '',
        description: 'Block javascript: URLs'
      },
      {
        input: 'data:text/html,<script>alert(1)</script>',
        expected: '',
        description: 'Block data: URLs'
      },
      {
        input: 'invalid-url',
        expected: '',
        description: 'Block invalid URLs'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = sanitizeUrl(input);
      const passed = result === expected;
      this.addResult(`URL Sanitization: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Test dangerous content detection
  testDangerousContentDetection() {
    console.log('\n🧪 Testing Dangerous Content Detection...');
    
    const testCases = [
      {
        input: '<script>alert("xss")</script>',
        expected: true,
        description: 'Detect script tags'
      },
      {
        input: '<iframe src="malicious.com"></iframe>',
        expected: true,
        description: 'Detect iframe tags'
      },
      {
        // eslint-disable-next-line no-script-url
        input: 'javascript:alert(1)',
        expected: true,
        description: 'Detect javascript: URLs'
      },
      {
        input: 'onclick=alert(1)',
        expected: true,
        description: 'Detect event handlers'
      },
      {
        input: 'Safe content here',
        expected: false,
        description: 'Safe content not detected as dangerous'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = containsDangerousContent(input);
      const passed = result === expected;
      this.addResult(`Dangerous Content Detection: ${description}`, passed, 
        passed ? undefined : `Expected: ${expected}, Got: ${result}`);
    });
  }

  // Test password strength validation
  testPasswordStrengthValidation() {
    console.log('\n🧪 Testing Password Strength Validation...');
    
    const testCases = [
      {
        input: 'Password123!',
        expectedValid: true,
        description: 'Strong password'
      },
      {
        input: 'password',
        expectedValid: false,
        description: 'Weak password (no uppercase, numbers)'
      },
      {
        input: 'PASSWORD',
        expectedValid: false,
        description: 'Weak password (no lowercase, numbers)'
      },
      {
        input: 'Pass123',
        expectedValid: false,
        description: 'Weak password (too short)'
      },
      {
        input: 'password123',
        expectedValid: false,
        description: 'Weak password (no uppercase)'
      }
    ];

    testCases.forEach(({ input, expectedValid, description }) => {
      const result = validatePasswordStrength(input);
      const passed = result.isValid === expectedValid;
      this.addResult(`Password Strength: ${description}`, passed, 
        passed ? undefined : `Expected: ${expectedValid}, Got: ${result.isValid}. Errors: ${result.errors.join(', ')}`);
    });
  }

  // Test URL safety validation
  testUrlSafetyValidation() {
    console.log('\n🧪 Testing URL Safety Validation...');
    
    const testCases = [
      {
        input: 'https://example.com',
        expected: true,
        description: 'Safe HTTPS URL'
      },
      {
        input: 'http://example.com',
        expected: true,
        description: 'Safe HTTP URL'
      },
      {
        // eslint-disable-next-line no-script-url
        input: 'javascript:alert(1)',
        expected: false,
        description: 'Unsafe javascript: URL'
      },
      {
        input: 'data:text/html,<script>alert(1)</script>',
        expected: false,
        description: 'Unsafe data: URL'
      },
      {
        input: 'file:///etc/passwd',
        expected: false,
        description: 'Unsafe file: URL'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = isSafeUrl(input);
      const passed = result === expected;
      this.addResult(`URL Safety: ${description}`, passed, 
        passed ? undefined : `Expected: ${expected}, Got: ${result}`);
    });
  }

  // Test secure random generation
  testSecureRandomGeneration() {
    console.log('\n🧪 Testing Secure Random Generation...');
    
    try {
      const random1 = generateSecureRandom(32);
      const random2 = generateSecureRandom(32);
      
      const passed = random1.length === 32 && random2.length === 32 && random1 !== random2;
      this.addResult('Secure Random Generation', passed, 
        passed ? undefined : 'Failed to generate secure random strings');
    } catch (error) {
      this.addResult('Secure Random Generation', false, `Error: ${error}`);
    }
  }

  // Test length validation
  testLengthValidation() {
    console.log('\n🧪 Testing Length Validation...');
    
    const testCases = [
      {
        input: 'Hello',
        minLength: 3,
        maxLength: 10,
        expected: true,
        description: 'Valid length'
      },
      {
        input: 'Hi',
        minLength: 3,
        maxLength: 10,
        expected: false,
        description: 'Too short'
      },
      {
        input: 'This is a very long string',
        minLength: 3,
        maxLength: 10,
        expected: false,
        description: 'Too long'
      },
      {
        input: '',
        minLength: 0,
        maxLength: 10,
        expected: true,
        description: 'Empty string with minLength 0'
      }
    ];

    testCases.forEach(({ input, minLength, maxLength, expected, description }) => {
      const result = validateLength(input, minLength, maxLength);
      const passed = result.isValid === expected;
      this.addResult(`Length Validation: ${description}`, passed, 
        passed ? undefined : `Expected: ${expected}, Got: ${result.isValid}. Error: ${result.error}`);
    });
  }

  // Test HTML escaping
  testHtmlEscaping() {
    console.log('\n🧪 Testing HTML Escaping...');
    
    const testCases = [
      {
        input: '<script>alert("xss")</script>',
        expected: '&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;',
        description: 'Escape script tags and quotes'
      },
      {
        input: 'Hello & World',
        expected: 'Hello &amp; World',
        description: 'Escape ampersands'
      },
      {
        input: 'Safe content',
        expected: 'Safe content',
        description: 'Safe content unchanged'
      }
    ];

    testCases.forEach(({ input, expected, description }) => {
      const result = escapeHtml(input);
      const passed = result === expected;
      this.addResult(`HTML Escaping: ${description}`, passed, 
        passed ? undefined : `Expected: "${expected}", Got: "${result}"`);
    });
  }

  // Run all tests
  runAllTests() {
    console.log('🚀 Starting Security Test Suite...\n');
    
    this.testHtmlSanitization();
    this.testTextSanitization();
    this.testEmailSanitization();
    this.testPhoneSanitization();
    this.testFilenameSanitization();
    this.testUrlSanitization();
    this.testDangerousContentDetection();
    this.testPasswordStrengthValidation();
    this.testUrlSafetyValidation();
    this.testSecureRandomGeneration();
    this.testLengthValidation();
    this.testHtmlEscaping();

    // Summary
    const totalTests = this.results.length;
    const passedTests = this.results.filter(r => r.passed).length;
    const failedTests = totalTests - passedTests;

    console.log('\n📊 Test Summary:');
    console.log(`Total Tests: ${totalTests}`);
    console.log(`✅ Passed: ${passedTests}`);
    console.log(`❌ Failed: ${failedTests}`);
    console.log(`Success Rate: ${((passedTests / totalTests) * 100).toFixed(1)}%`);

    if (failedTests > 0) {
      console.log('\n❌ Failed Tests:');
      this.results.filter(r => !r.passed).forEach(result => {
        console.log(`- ${result.testName}: ${result.error}`);
      });
    }

    return {
      total: totalTests,
      passed: passedTests,
      failed: failedTests,
      successRate: (passedTests / totalTests) * 100,
      results: this.results
    };
  }
}

// Export for use in other files
export { SecurityTestSuite };

